/**
 * Official image URLs (hotlinked from the NBA / ESPN CDNs, never re-hosted) and the
 * pure fallbacks the UI uses when one fails to load. No React here — unit-tested.
 */
import { nbaTeamId, type GameMeta, type Side } from '@clutch/core';

export type ImageSource = GameMeta['source'] | 'espn';

export { NBA_TEAM_IDS } from '@clutch/core';

/** ESPN's short codes → NBA tricodes (ESPN uses GS, NY, SA, NO, UTAH, WSH). */
const ESPN_ALIASES: Record<string, string> = { GS: 'GSW', NY: 'NYK', SA: 'SAS', NO: 'NOP', UTAH: 'UTA', WSH: 'WAS', PHO: 'PHX', BRK: 'BKN' };

export function normalizeTricode(code: string): string {
  const c = code.trim().toUpperCase();
  return ESPN_ALIASES[c] ?? c;
}

/**
 * NBA logo SVG for a team, always by tricode (`nbaTeamId`): ESPN-source games carry ESPN team ids,
 * so a meta's own `teamId` is never trusted for NBA assets.
 */
export function teamLogoUrl(team: { tricode: string; teamId?: number }): string | undefined {
  const id = nbaTeamId(normalizeTricode(team.tricode));
  return id ? `https://cdn.nba.com/logos/nba/${id}/primary/L/logo.svg` : undefined;
}

export function sideTeam(meta: Pick<GameMeta, 'home' | 'away'>, side: Side) {
  return side === 'home' ? meta.home : meta.away;
}

/**
 * Player headshot. NBA archive games carry NBA personIds; ESPN-source games carry ESPN athlete ids.
 * `undefined` when there is no id (team plays, fixtures without people).
 */
export function headshotUrl(personId: number | undefined, source: ImageSource = 'public_research_archive'): string | undefined {
  if (typeof personId !== 'number' || !Number.isFinite(personId) || personId <= 0) return undefined;
  if (source === 'espn_public' || source === 'espn') return `https://a.espncdn.com/i/headshots/nba/players/full/${personId}.png`;
  return `https://cdn.nba.com/headshots/nba/latest/1040x760/${personId}.png`;
}

/**
 * Same image through the app's same-origin pass-through (`/cdn/nba/...`, see vite.config.ts), for the
 * canvas reel only: cdn.nba.com sends no CORS header, and a cross-origin image would taint the canvas
 * and block `captureStream`. ESPN sends `access-control-allow-origin: *`, so it is used directly.
 */
export function canvasSafeUrl(url: string | undefined): string | undefined {
  if (!url) return undefined;
  if (url.startsWith('https://cdn.nba.com/')) return `/cdn/nba/${url.slice('https://cdn.nba.com/'.length)}`;
  return url;
}

/** The fallback mark when an image fails: initials of a player ("BRUNSON" → "BR", "Jalen Johnson" → "JJ"). */
export function nameInitials(name: string | undefined): string {
  const parts = (name ?? '').replace(/[^\p{L}\s'-]/gu, ' ').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

/** Remember failures for the session so a broken URL isn't retried on every render. */
const failed = new Set<string>();
export const imageFailed = (url: string | undefined) => !url || failed.has(url);
export const markImageFailed = (url: string) => failed.add(url);
export const resetImageFailures = () => failed.clear();
